import { useMutation, useQuery } from '@apollo/client'
import { AddCircle, Delete, Edit } from '@mui/icons-material'
import { Button, Grid, IconButton, Typography } from '@mui/material'
import ToggleEditAutocomplete from 'candidate/components/ToggleEditFormFields/ToggleEditAutocomplete'
import ToggleEditDatePicker from 'candidate/components/ToggleEditFormFields/ToggleEditDatePicker'
import {
    EditButtonContainer,
    StyledForm,
    SubtitleContainer,
    TitleContainer,
} from 'candidate/components/styles'
import { CandidateEducationOptionsQuery } from 'candidate/graphql/profile.gql'
import { formatDate } from 'date-fns'
import isEqual from 'lodash.isequal'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import useToast from 'shared/components/Toast/useToast'
import { UpdateSelfEducationExperience } from 'shared/graphql/candidateProfile.gql'
import { getPxAsRem } from 'shared/utils'
import { MMyyyyToValidDateString } from 'shared/utils/dateFormatter'
import { PROFILE_SECTIONS } from './types'

import { CandidateEducationOptionsQueryQuery } from '__generated__/graphql'
import type { FC } from 'react'
import type { DropdownOption } from 'shared/models/dropdownOption'
import type { FormComponentProps } from './types'

export interface EducationFormValues {
    id?: string
    candidateId?: string
    educationType: DropdownOption | undefined
    college: DropdownOption | undefined
    majors: DropdownOption[]
    graduationDate: string
    gpa: DropdownOption | undefined
    gpaScale: DropdownOption | undefined
}

const Education: FC<FormComponentProps> = ({
    data,
    loading,
    refetch,
    edit,
    setEdit,
    closeEditWithoutModal,
    setUnsavedChangesExist,
}) => {
    const toast = useToast()

    const educationOptions = useQuery<CandidateEducationOptionsQueryQuery>(
        CandidateEducationOptionsQuery
    )
    /**
     * Populate the form with the candidate's education data
     */
    const education = data.candidate?.education
    const initialFormValues: EducationFormValues[] = useMemo(
        () =>
            education?.map((val) => ({
                id: val.id,
                college: {
                    id: val.college?.id ?? '',
                    value: val.college?.value ?? '',
                },
                candidateId: data.candidate?.id ?? '',
                graduationDate: MMyyyyToValidDateString(
                    val.graduationDate ?? ''
                ),
                educationType: val.degreeTypeId
                    ? { id: 'graduate', value: 'Graduate' }
                    : { id: 'undergraduate', value: 'Undergraduate' },
                majors: val.degreeTypeId
                    ? val?.degrees ?? []
                    : val?.majors?.map((major) => ({
                          id: major.majorId ?? '',
                          value: major.major?.value ?? '',
                      })) ?? [],
                gpa: val.gpa
                    ? {
                          id: val.gpa?.toFixed(1).toString() ?? '',
                          value: val.gpa?.toFixed(1).toString() ?? '',
                      }
                    : undefined,
                gpaScale: val.gpaScale
                    ? {
                          id: val.gpaScale?.toFixed(1).toString() ?? '',
                          value: val.gpaScale?.toFixed(1).toString() ?? '',
                      }
                    : undefined,
            })) ?? [],
        [education, data.candidate]
    )

    const [educationFormValues, setEducationFormValues] =
        useState<EducationFormValues[]>(initialFormValues)
    const emptyErrorState = useMemo(
        () =>
            education?.map(() => ({
                educationType: '',
                college: '',
                majors: '',
                graduationDate: '',
                gpa: '',
                gpaScale: '',
            })) ?? [],
        [education]
    )
    const [educationErrorValues, setEducationErrorValues] =
        useState(emptyErrorState)

    const [mutate] = useMutation(UpdateSelfEducationExperience)

    const handleEditClick = () => {
        if (edit) {
            setEdit(null)
        } else {
            setEdit(PROFILE_SECTIONS.EDUCATION)
        }
    }

    const resetForm = useCallback(() => {
        setEducationFormValues(initialFormValues)
        setEducationErrorValues(emptyErrorState)
    }, [initialFormValues, emptyErrorState])

    const handleChange = (nextValue, index, field) => {
        setEducationFormValues((prev) => {
            const next = [...prev]
            const newObj = { ...next[index] }
            newObj[field] = nextValue
            next[index] = newObj
            return next
        })
    }

    const handleErrorChange = (nextValue, index, field) => {
        setEducationErrorValues((prev) => {
            const next = [...prev]
            const newObj = { ...next[index] }
            newObj[field] = nextValue
            next[index] = newObj
            return next
        })
    }

    const handleAddDegree = () => {
        if (!edit) setEdit(PROFILE_SECTIONS.EDUCATION)
        setEducationFormValues((prev) => [
            ...prev,
            {
                educationType: { id: 'undergraduate', value: 'Undergraduate' },
                college: undefined,
                majors: [],
                graduationDate: '',
                gpa: undefined,
                gpaScale: undefined,
            },
        ])
        setEducationErrorValues((prev) => [
            ...prev,
            {
                educationType: '',
                college: '',
                majors: '',
                graduationDate: '',
                gpa: '',
                gpaScale: '',
            },
        ])
    }
    const determineMajorOptions = (
        educationType: DropdownOption<void> | undefined
    ) => {
        const options = (educationType?.id === 'undergraduate'
            ? educationOptions.data?.majors
            : educationOptions.data?.degreeTypes) || [{ id: '', value: '' }]

        return options
    }

    const determineCollegeOptions = (
        educationType: DropdownOption<void> | undefined,
        index: number
    ): DropdownOption[] => {
        if (educationType?.id === 'graduate' && educationFormValues) {
            const hasMBA =
                educationFormValues[index]?.majors?.find(
                    (i) => i.value === 'MBA'
                ) ?? false

            if (hasMBA) {
                return educationOptions.data?.mbaColleges || []
            }
        }

        return educationOptions.data?.colleges || []
    }

    const handleRemove = (index) => {
        if (!edit) setEdit(PROFILE_SECTIONS.EDUCATION)
        setEducationFormValues((prev) => {
            const next = [...prev]
            next.splice(index, 1)
            return next
        })
        setEducationErrorValues((prev) => {
            const next = [...prev]
            next.splice(index, 1)
            return next
        })
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        try {
            const response = await mutate({
                variables: {
                    educationEntries: educationFormValues.map((education) => ({
                        id: education.id,
                        degreeIds:
                            education?.majors?.map((major) => major.id) || [],
                        graduationDate: formatDate(
                            education.graduationDate,
                            'MM-yyyy'
                        ),
                        gpa: !!education?.gpa?.value
                            ? +education.gpa.value
                            : undefined,
                        gpaScale: !!education?.gpaScale?.value
                            ? +education.gpaScale.value
                            : undefined,
                        collegeId: education.college?.id || '',
                    })),
                },
            })
            if (response.data?.updateSelfEducationExperience.success) {
                toast({
                    title: 'Success!',
                    message: 'Education has been updated',
                    severity: 'success',
                })
                closeEditWithoutModal()
                refetch()
            } else {
                toast({
                    title: 'Unable to update education',
                    message: 'Please try again.',
                    severity: 'error',
                })
            }
        } catch (error) {
            console.error(error)
            toast({
                title: 'Unable to update education',
                message: 'Please try again.',
                severity: 'error',
            })
        }
    }

    const gpaChoices = useMemo(() => {
        const gpaChoices: DropdownOption[] = []
        for (let i = 5.0; i >= 2.0; i -= 0.1) {
            const choice: DropdownOption = {
                id: i.toFixed(1).toString(),
                value: i.toFixed(1).toString(),
            }
            gpaChoices.push(choice)
        }
        gpaChoices.push({ id: '0.0', value: 'N/A' })
        return gpaChoices
    }, [])

    const gpaScaleChoices = useMemo(
        () => [
            { id: '4.0', value: '4.0' },
            { id: '4.3', value: '4.3' },
            { id: '5.0', value: '5.0' },
        ],
        []
    )

    const unsavedChangesExist = useMemo(() => {
        if (educationFormValues.length !== education?.length) return true
        if (
            !isEqual(
                educationFormValues,
                education.map((val) => ({
                    ...val,
                    graduationDate: MMyyyyToValidDateString(
                        val.graduationDate ?? ''
                    ),
                    educationType: val.degreeTypeId
                        ? { id: 'graduate', value: 'Graduate' }
                        : { id: 'undergraduate', value: 'Undergraduate' },
                    majors: val.degreeTypeId
                        ? val.degrees
                        : val?.majors?.map(({ major }) => major) ?? [],
                    gpa: {
                        id: val.gpa?.toFixed(1).toString(),
                        value: val.gpa?.toFixed(1).toString(),
                    },
                    gpaScale: {
                        id: val.gpaScale?.toFixed(1).toString(),
                        value: val.gpaScale?.toFixed(1).toString(),
                    },
                }))
            )
        )
            return true
        return false
    }, [educationFormValues, education])

    const isDisabled = useMemo(() => {
        if (!unsavedChangesExist) return true
        const hasEmptyValues = educationFormValues.reduce((acc, formValue) => {
            if (acc) {
                return acc
            } else {
                return Object.values(formValue).some(
                    (value) =>
                        value === '' || isEqual(value, { id: '', value: '' })
                )
            }
        }, false)
        if (hasEmptyValues) return true
        const hasErrors = educationErrorValues.reduce((acc, formValue) => {
            if (acc) {
                return acc
            } else {
                return Object.values(formValue).some((value) => value !== '')
            }
        }, false)
        return hasErrors
    }, [educationFormValues, educationErrorValues, unsavedChangesExist])

    useEffect(() => {
        setUnsavedChangesExist(unsavedChangesExist)
    }, [unsavedChangesExist, setUnsavedChangesExist])

    useEffect(() => {
        if (!edit) resetForm()
    }, [edit, resetForm])

    return (
        <>
            <TitleContainer>
                <Typography variant="h6" component="h2">
                    Education
                </Typography>
                <IconButton onClick={handleEditClick} disabled={loading}>
                    <Edit fontSize="small" />
                </IconButton>
            </TitleContainer>
            <StyledForm onSubmit={handleSubmit}>
                {educationFormValues.map(
                    (
                        {
                            id,
                            educationType,
                            college,
                            majors,
                            graduationDate,
                            gpa,
                            gpaScale,
                        },
                        index
                    ) => (
                        <Grid
                            key={id || index}
                            container
                            rowSpacing={3}
                            columnSpacing={2}
                            sx={{ marginBottom: getPxAsRem(32) }}
                        >
                            <SubtitleContainer item xs={12}>
                                <Typography variant="subtitle2">
                                    Degree {index + 1}
                                </Typography>
                                {(index !== 0 ||
                                    educationFormValues?.length > 1) && (
                                    <IconButton
                                        onClick={() => handleRemove(index)}
                                        aria-label="remove work experience"
                                    >
                                        <Delete />
                                    </IconButton>
                                )}
                            </SubtitleContainer>
                            <Grid item xs={12}>
                                <ToggleEditAutocomplete
                                    label="Degree Type"
                                    options={[
                                        { id: 'graduate', value: 'Graduate' },
                                        {
                                            id: 'undergraduate',
                                            value: 'Undergraduate',
                                        },
                                    ]}
                                    edit={edit}
                                    labelId="educationType"
                                    value={educationType}
                                    helperText={
                                        educationErrorValues[index]
                                            .educationType
                                    }
                                    error={
                                        !!educationErrorValues[index]
                                            .educationType
                                    }
                                    onBlur={(
                                        e: React.FocusEvent<HTMLInputElement>
                                    ) => {
                                        if (!e.target.value) {
                                            handleErrorChange(
                                                'Degree type is required',
                                                index,
                                                'educationType'
                                            )
                                        }
                                    }}
                                    onChange={(event, nextValue) => {
                                        if (nextValue) {
                                            handleChange(
                                                nextValue,
                                                index,
                                                'educationType'
                                            )
                                            //Reset to handle for school changes when selecting MBA.
                                            handleChange(null, index, 'majors')
                                            handleChange(null, index, 'college')
                                            handleErrorChange(
                                                '',
                                                index,
                                                'educationType'
                                            )
                                        } else {
                                            handleChange(
                                                { id: '', value: '' },
                                                index,
                                                'educationType'
                                            )
                                            handleErrorChange(
                                                'Degree type is required',
                                                index,
                                                'educationType'
                                            )
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                {educationType?.id && (
                                    <ToggleEditAutocomplete
                                        multiple
                                        edit={edit}
                                        label={
                                            educationType?.id ===
                                            'undergraduate'
                                                ? 'Major'
                                                : 'Degree'
                                        }
                                        labelId="majors"
                                        helperText={
                                            educationErrorValues[index].majors
                                        }
                                        error={
                                            !!educationErrorValues[index].majors
                                        }
                                        options={determineMajorOptions(
                                            educationType
                                        )}
                                        getOptionDisabled={(option) => {
                                            return (
                                                educationType?.id ===
                                                    'graduate' &&
                                                (majors?.length || 0) >= 1
                                            )
                                        }}
                                        onBlur={(
                                            e: React.FocusEvent<HTMLInputElement>
                                        ) => {
                                            if (majors?.length === 0) {
                                                handleErrorChange(
                                                    'Majors are required',
                                                    index,
                                                    'majors'
                                                )
                                            }
                                        }}
                                        value={majors ?? []}
                                        onChange={(event, newValue) => {
                                            if (newValue) {
                                                handleErrorChange(
                                                    '',
                                                    index,
                                                    'majors'
                                                )
                                            } else {
                                                handleErrorChange(
                                                    'Major is required',
                                                    index,
                                                    'majors'
                                                )
                                            }
                                            handleChange(
                                                newValue,
                                                index,
                                                'majors'
                                            )
                                        }}
                                    />
                                )}
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <ToggleEditAutocomplete
                                    label="College/University"
                                    options={determineCollegeOptions(
                                        educationType,
                                        index
                                    )}
                                    edit={edit}
                                    labelId="college"
                                    helperText={
                                        educationErrorValues[index].college
                                    }
                                    error={
                                        !!educationErrorValues[index].college
                                    }
                                    value={college}
                                    onBlur={(
                                        e: React.FocusEvent<HTMLInputElement>
                                    ) => {
                                        if (!e.target.value) {
                                            handleErrorChange(
                                                'College is required',
                                                index,
                                                'college'
                                            )
                                        }
                                    }}
                                    onChange={(event, nextValue) => {
                                        if (nextValue) {
                                            handleChange(
                                                nextValue,
                                                index,
                                                'college'
                                            )
                                            handleErrorChange(
                                                '',
                                                index,
                                                'college'
                                            )
                                        } else {
                                            handleChange(
                                                { id: '', value: '' },
                                                index,
                                                'college'
                                            )
                                            handleErrorChange(
                                                'College is required',
                                                index,
                                                'college'
                                            )
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <ToggleEditDatePicker
                                    label="Graduation Date"
                                    views={['month', 'year']}
                                    name="graduationDate"
                                    helperText={
                                        educationErrorValues[index]
                                            .graduationDate
                                    }
                                    error={
                                        !!educationErrorValues[index]
                                            .graduationDate
                                    }
                                    edit={edit}
                                    value={
                                        !!graduationDate
                                            ? new Date(graduationDate)
                                            : undefined
                                    }
                                    onBlur={(
                                        e: React.FocusEvent<HTMLInputElement>
                                    ) => {
                                        if (e.target.value === 'MM/DD/YYYY') {
                                            handleErrorChange(
                                                'Graduation date is required',
                                                index,
                                                'graduationDate'
                                            )
                                        }
                                    }}
                                    onChange={(date) => {
                                        if (date) {
                                            handleChange(
                                                date,
                                                index,
                                                'graduationDate'
                                            )
                                            handleErrorChange(
                                                '',
                                                index,
                                                'graduationDate'
                                            )
                                        } else {
                                            handleChange(
                                                '',
                                                index,
                                                'graduationDate'
                                            )
                                            handleErrorChange(
                                                'Graduation date is required',
                                                index,
                                                'graduationDate'
                                            )
                                        }
                                    }}
                                    format="MM/yyyy"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <ToggleEditAutocomplete
                                    edit={edit}
                                    label="Commutative GPA"
                                    labelId="commutativeGPA"
                                    helperText={educationErrorValues[index].gpa}
                                    error={!!educationErrorValues[index].gpa}
                                    options={gpaChoices}
                                    value={gpaChoices.find(
                                        (choice) => choice?.id === gpa?.id
                                    )}
                                    onBlur={(
                                        e: React.FocusEvent<HTMLInputElement>
                                    ) => {
                                        if (!e.target.value) {
                                            handleErrorChange(
                                                'GPA is required',
                                                index,
                                                'gpa'
                                            )
                                        }
                                    }}
                                    onChange={(event, newValue) => {
                                        if (!Array.isArray(newValue)) {
                                            if (newValue) {
                                                handleChange(
                                                    newValue,
                                                    index,
                                                    'gpa'
                                                )
                                                handleErrorChange(
                                                    '',
                                                    index,
                                                    'gpa'
                                                )
                                                if (
                                                    parseFloat(newValue.id) >
                                                    parseFloat(
                                                        gpaScale?.id || '0.0'
                                                    )
                                                ) {
                                                    handleErrorChange(
                                                        'GPA cannot be higher than the GPA scale',
                                                        index,
                                                        'gpa'
                                                    )
                                                }
                                            } else {
                                                handleChange(
                                                    { id: '', value: '' },
                                                    index,
                                                    'gpa'
                                                )
                                                handleErrorChange(
                                                    'GPA is required',
                                                    index,
                                                    'gpa'
                                                )
                                            }
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <ToggleEditAutocomplete
                                    edit={edit}
                                    label="GPA Scale"
                                    options={gpaScaleChoices}
                                    value={gpaScaleChoices.find(
                                        (choice) => choice?.id === gpaScale?.id
                                    )}
                                    labelId="gpaScale"
                                    helperText={
                                        educationErrorValues[index].gpaScale
                                    }
                                    error={
                                        !!educationErrorValues[index].gpaScale
                                    }
                                    onBlur={(
                                        e: React.FocusEvent<HTMLInputElement>
                                    ) => {
                                        if (!e.target.value) {
                                            handleErrorChange(
                                                'GPA Scale is required',
                                                index,
                                                'gpaScale'
                                            )
                                        }
                                    }}
                                    onChange={(event, newValue) => {
                                        if (!Array.isArray(newValue)) {
                                            if (newValue) {
                                                handleChange(
                                                    newValue,
                                                    index,
                                                    'gpaScale'
                                                )
                                                handleErrorChange(
                                                    '',
                                                    index,
                                                    'gpaScale'
                                                )
                                                if (
                                                    parseFloat(newValue.id) >=
                                                    parseFloat(gpa?.id || '0.0')
                                                ) {
                                                    handleErrorChange(
                                                        '',
                                                        index,
                                                        'gpa'
                                                    )
                                                } else {
                                                    handleErrorChange(
                                                        'GPA cannot be higher than the GPA scale',
                                                        index,
                                                        'gpa'
                                                    )
                                                }
                                            } else {
                                                handleChange(
                                                    { id: '', value: '' },
                                                    index,
                                                    'gpaScale'
                                                )
                                                handleErrorChange(
                                                    'GPA Scale is required',
                                                    index,
                                                    'gpaScale'
                                                )
                                            }
                                        }
                                    }}
                                />
                            </Grid>
                            {index === educationFormValues.length - 1 && (
                                <EditButtonContainer item xs={12} md={6}>
                                    <Button
                                        onClick={handleAddDegree}
                                        variant="text"
                                        startIcon={<AddCircle />}
                                    >
                                        Add Degree
                                    </Button>
                                </EditButtonContainer>
                            )}
                        </Grid>
                    )
                )}
                {!educationFormValues.length && (
                    <EditButtonContainer item xs={12} md={6}>
                        <Button
                            onClick={handleAddDegree}
                            variant="text"
                            startIcon={<AddCircle />}
                        >
                            Add Degree
                        </Button>
                    </EditButtonContainer>
                )}
                {edit && (
                    <EditButtonContainer item xs={12}>
                        <Button
                            variant="outlined"
                            type="reset"
                            onClick={() => {
                                resetForm()
                                closeEditWithoutModal()
                            }}
                        >
                            Cancel
                        </Button>
                        <Button type="submit" disabled={isDisabled}>
                            Save and Update
                        </Button>
                    </EditButtonContainer>
                )}
            </StyledForm>
        </>
    )
}

export default Education
